import SchoolOfficerApi from "@/api/SchoolOfficerApi";
import SchoolOfficer from "@/models/APIModels/SchoolOfficer";

import SchoolOfficerViewModel from "@/models/ViewModels/SchoolOfficerModel";
import TokenUseCase from "./TokenUseCase";
import PaymentSourceModel from "@/models/ViewModels/PaymentSourceModel";
import { isObjectValueEqual } from "@/shared/helperFunctions";
import PaymentTokenizeUseCase from "./PaymentTokenizeUseCase";
import store from "@/store";

export default class SchoolOfficerUseCase {
  private readonly _schoolOfficerApi: SchoolOfficerApi;

  constructor(schoolOfficerApi = new SchoolOfficerApi()) {
    this._schoolOfficerApi = schoolOfficerApi;
  }

  async insert(
    schoolOfficer: SchoolOfficerViewModel,
    updateToken: boolean = false
  ) {
    try {
      const result = await this._schoolOfficerApi.insert(
        this.mapSchoolOfficerFromViewModel(schoolOfficer)
      );

      if (result.content) {
        result.content.user = this.mapSchoolOfficerToViewModel(
          result.content.user
        );

        if (updateToken) {
          const tokenUseCase = new TokenUseCase();
          await tokenUseCase.setAccessToken(result.content.accessToken);
          store.commit("mutStoreCurrentUser", result.content.user);
        }
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async update(schoolOfficer: SchoolOfficerViewModel, userReference: string) {
    try {
      const result = await this._schoolOfficerApi.update(
        this.mapSchoolOfficerFromViewModel(schoolOfficer),
        userReference
      );
      if (result.content)
        result.content = this.mapSchoolOfficerToViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getAll() {
    try {
      const result = await this._schoolOfficerApi.getAll();
      if (result.content)
        result.content = result.content.map((item) =>
          this.mapSchoolOfficerToViewModel(item)
        );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getBySchool(
    schoolReference: string,
    schoolOfficerStatus: string[],
    sortBy: string
  ) {
    try {
      let includeInactiveOfficers = false,
        includeActiveOfficers = false,
        sortByQuery: string = "";
      schoolOfficerStatus.forEach((status) => {
        if (status === "Active") includeActiveOfficers = true;
        if (status === "Inactive") includeInactiveOfficers = true;
      });
      if (sortBy) sortByQuery = `&sortBy=${sortBy}`;

      const result = await this._schoolOfficerApi.getBySchool(
        schoolReference,
        includeActiveOfficers,
        includeInactiveOfficers,
        sortByQuery
      );
      if (result.content)
        result.content = result.content.map((item) =>
          this.mapSchoolOfficerToViewModel(item)
        );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getByReference(userReference: string) {
    try {
      const result = await this._schoolOfficerApi.getByReference(userReference);

      if (result.content)
        result.content = this.mapSchoolOfficerToViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async requestBankAccountChangeSMSCode(userReference: string) {
    try {
      const result =
        await this._schoolOfficerApi.requestBankAccountChangeSMSCode(
          userReference
        );

      return result;
    } catch (error) {
      throw error;
    }
  }

  async authoriseBankAccountChange(
    userReference: string,
    verificationCode: string
  ) {
    try {
      const result = await this._schoolOfficerApi.authoriseBankAccountChange(
        userReference,
        verificationCode
      );

      if (result.content) {
        const tokenUseCase = new TokenUseCase();

        await tokenUseCase.setAccessToken(result.content.accessToken);
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async establishAccess(userReference: string) {
    try {
      const result = await this._schoolOfficerApi.establishAccess(
        userReference
      );

      return result;
    } catch (error) {
      throw error;
    }
  }

  async activateSchoolOfficer(userReference: string, concurrency: string) {
    try {
      const result = await this._schoolOfficerApi.activateSchoolOfficer(
        userReference,
        concurrency
      );

      if (result.content)
        result.content = this.mapSchoolOfficerFromViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async deactivateSchoolOfficer(userReference: string, concurrency: string) {
    try {
      const result = await this._schoolOfficerApi.deactivateSchoolOfficer(
        userReference,
        concurrency
      );

      if (result.content)
        result.content = this.mapSchoolOfficerFromViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async resendEmail(userReference: string) {
    try {
      const result = await this._schoolOfficerApi.resendEmail(userReference);

      return result;
    } catch (error) {
      throw error;
    }
  }

  async setPaymentSource(
    paymentSource: PaymentSourceModel,
    schoolOfficerReference: string,
    concurrencyRV: string
  ) {
    try {
      const result = await this._schoolOfficerApi.setPaymentSource(
        paymentSource,
        schoolOfficerReference,
        concurrencyRV
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async chargeAccount(userReference: string) {
    try {
      const result = await this._schoolOfficerApi.chargeAccount(userReference);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getPaymentList(filter: {
    userReference?: string;
    fromDate?: Date;
    toDate?: Date;
  }) {
    let queryStringArray: string[] = [];
    if (filter.userReference)
      queryStringArray.push(`userReference=${filter.userReference}`);
    if (filter.fromDate) queryStringArray.push(`fromDate=${filter.fromDate}`);
    if (filter.toDate) queryStringArray.push(`toDate=${filter.toDate}`);
    const queryString = queryStringArray.join("&");
    const result = await this._schoolOfficerApi.getPaymentList(queryString);

    return result;
  }

  private mapSchoolOfficerToViewModel(
    schoolOfficer: SchoolOfficer
  ): SchoolOfficerViewModel {
    return { ...schoolOfficer };
  }
  private mapSchoolOfficerFromViewModel(
    schoolOfficerViewModel: SchoolOfficerViewModel
  ): SchoolOfficer {
    return { ...schoolOfficerViewModel };
  }
}
